/**
 * browserSupport.js — strict detection of installable-PWA browsers.
 *
 * We ONLY show the "Install this WebApp" banner where installation is
 * genuinely supported per our product matrix:
 *   • iOS / iPadOS mobile  → Safari only (Chrome iOS uses WebKit but has
 *     no "Add to Home Screen" flow — hiding avoids confusing guests)
 *   • Android mobile       → Chrome only (Samsung/Firefox also expose
 *     `beforeinstallprompt` but their install UX diverges enough that
 *     the host asked to restrict to Chrome)
 *   • Desktop              → Safari (macOS 14+ "Add to Dock") or Chrome
 *
 * All other combinations return `false` so the promo never appears.
 */

export const isIOS = () =>
  typeof navigator !== "undefined"
  && /iPad|iPhone|iPod/.test(navigator.userAgent)
  && !window.MSStream;

export const isAndroid = () =>
  typeof navigator !== "undefined" && /Android/.test(navigator.userAgent);

export const isMobile = () => isIOS() || isAndroid();

export const isSafari = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // Real Safari sends "Safari/…" and doesn't include Chrome/Chromium markers.
  return /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|Edg|OPR|SamsungBrowser/.test(ua);
};

export const isChrome = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // Chrome desktop / Chrome Android — exclude derivative Chromium browsers.
  return (/Chrome/.test(ua) || /CriOS/.test(ua))
    && !/Edg|OPR|SamsungBrowser|YaBrowser|UCBrowser/.test(ua);
};

/**
 * True only in browsers where our PWA install flow works properly.
 * See file header for the support matrix.
 */
export const canInstallPWA = () => {
  if (typeof navigator === "undefined") return false;
  if (isIOS()) return isSafari();
  if (isAndroid()) return isChrome();
  // Desktop: allow Chrome (beforeinstallprompt) or Safari (Add to Dock).
  return isSafari() || isChrome();
};

/** Convenience: distinguishes desktop Safari (needs "Add to Dock" hint) */
export const isDesktopSafari = () =>
  !isMobile() && isSafari();

export const isDesktop = () => !isMobile();

/** Chrome on iOS (WebKit under the hood, UA contains "CriOS"). iOS 16.4+
 *  supports "Add to Home Screen" from Chrome's own Share menu, so we must
 *  NOT redirect these users to Safari. */
export const isIOSChrome = () =>
  isIOS() && typeof navigator !== "undefined" && /CriOS/.test(navigator.userAgent);

export const isEdge = () =>
  typeof navigator !== "undefined" && /Edg\//.test(navigator.userAgent);

export const isFirefox = () =>
  typeof navigator !== "undefined" && /Firefox|FxiOS/.test(navigator.userAgent);

export const isSamsungBrowser = () =>
  typeof navigator !== "undefined" && /SamsungBrowser/.test(navigator.userAgent);

/** macOS Safari (desktop) — supports "Add to Dock" on Sonoma+. */
export const isMacSafari = () =>
  !isMobile() && isSafari()
  && typeof navigator !== "undefined" && /Macintosh/.test(navigator.userAgent);

/** In-app webviews (WhatsApp / Instagram / Facebook / Messenger / TikTok).
 *  These cannot install a PWA — we must invite the user to open the page in
 *  the real system browser instead. */
export const isInAppBrowser = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /\b(FBAN|FBAV|FB_IAB|Instagram|Line|WhatsApp|Messenger|TikTok|Twitter|Snapchat)\b/i.test(ua)
    // Generic Android webview marker (wv) often used by in-app browsers.
    || (/Android/.test(ua) && /; wv\)/.test(ua));
};

export const isStandalonePWA = () =>
  typeof window !== "undefined"
  && (window.matchMedia?.("(display-mode: standalone)")?.matches
      || window.navigator?.standalone === true);
